import React, { useEffect, useState, useRef } from "react";
import { database, ref, onValue } from "./firebase";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
  LabelList,
} from "recharts";
import BossNavigation from "./BossNavigation";
import "./BillingSystem.css";

export default function CustomerSalesInsights() {
  const [customer1, setCustomer1] = useState("");
  const [customer2, setCustomer2] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [data, setData] = useState([]);
  const sectionRef = useRef();

  const shortenProductName = (name) => {
    return name
      .replace("Green ", "G ")
      .replace("Premium ", "P ")
      .replace("Bottle", "")
      .replace("Pouch", "Pouch")
      .replace("Jar", "Jar")
      .replace("Packed Ice Cube", "Ice")
      .trim();
  };

  const parseAnyDate = (rawDate) => {
    if (!rawDate || typeof rawDate !== "string") return null;
    const parts = rawDate.split(/[\/\-]/);
    if (parts.length !== 3) return null;
    let year, month, day;
    if (parts[0].length === 4) {
      [year, month, day] = parts;
    } else {
      [day, month, year] = parts;
    }
    const date = new Date(`${year}-${month}-${day}`);
    return isNaN(date.getTime()) ? null : date;
  };

  const isCustomerMatch = (target, actual) => {
    if (!target || !actual) return false;
    return actual.toLowerCase().includes(target.toLowerCase()) ||
           target.toLowerCase().includes(actual.toLowerCase());
  };

  useEffect(() => {
    const c1 = customer1.trim().toLowerCase();
    const c2 = customer2.trim().toLowerCase();
    if (c1.length < 3 || c1.includes(" ")) return;

    const invoicesRef = ref(database, "invoices");
    onValue(invoicesRef, (snapshot) => {
      const allInvoices = snapshot.val() || {};
      const productMap = {};
      const from = fromDate ? parseAnyDate(fromDate) : null;
      const to = toDate ? parseAnyDate(toDate) : null;

      Object.values(allInvoices).forEach((invoice) => {
        const invoiceDate = parseAnyDate(invoice.date);
        const name = (invoice.customerName || "").trim().toLowerCase();
        if (
          invoiceDate &&
          (!from || invoiceDate >= from) &&
          (!to || invoiceDate <= to) &&
          (isCustomerMatch(c1, name) || (c2 && isCustomerMatch(c2, name)))
        ) {
          invoice.items?.forEach((item) => {
            const key = item.product;
            const shortName = shortenProductName(key);
            const quantity = Number(item.quantity || 0);
            if (!productMap[shortName]) {
              productMap[shortName] = { product: shortName };
            }
            if (isCustomerMatch(c1, name)) {
              productMap[shortName][c1] = (productMap[shortName][c1] || 0) + quantity;
            }
            if (c2 && isCustomerMatch(c2, name)) {
              productMap[shortName][c2] = (productMap[shortName][c2] || 0) + quantity;
            }
          });
        }
      });
      setData(Object.values(productMap));
    });
  }, [customer1, customer2, fromDate, toDate]);

  const handlePrint = () => {
    const printContents = sectionRef.current.innerHTML;
    const win = window.open();
    if (!win) return alert("Please allow popup to print.");
    win.document.write(`
      <html>
        <head>
          <title>Print</title>
          <style>
            @media print {
              @page {
                size: A4 Portrait;
                margin: 0.5cm;
              }
              html, body {
                width: 100%;
                height: auto;
                font-family: Arial, sans-serif;
                font-size: 12px;
                -webkit-print-color-adjust: exact;
              }
              .stock-table {
                width: 100%;
                border-collapse: collapse;
                table-layout: fixed;
                word-wrap: break-word;
                page-break-inside: avoid;
              }
              .stock-table th, .stock-table td {
                border: 1px solid #000;
                padding: 6px;
                text-align: center;
              }
              .recharts-wrapper {
                width: 100% !important;
                height: 400px !important;
              }
              .button, input, label, .flex-row, .section:first-child {
                display: none !important;
              }
            }
          </style>
        </head>
        <body>${printContents}</body>
      </html>
    `);
    win.document.close();
    win.print();
  };

  const c1 = customer1.trim().toLowerCase();
  const c2 = customer2.trim().toLowerCase();
  const totals = {
    [c1]: data.reduce((sum, d) => sum + (d[c1] || 0), 0),
    [c2]: data.reduce((sum, d) => sum + (d[c2] || 0), 0),
  };

  return (
    <div className="container">
      <h2 className="page-heading">📈 Customer Sales Insights</h2>
      <BossNavigation />

      <div className="section flex-col gap-2">
        <div className="flex-row gap-2">
          <label>
            Customer 1 (required):
            <input
              className="input"
              value={customer1}
              onChange={(e) => setCustomer1(e.target.value)}
              required
            />
          </label>
          <label>
            Customer 2 (optional):
            <input
              className="input"
              value={customer2}
              onChange={(e) => setCustomer2(e.target.value)}
            />
          </label>
        </div>
        <div className="flex-row gap-2">
          <label>
            From:
            <input
              type="date"
              className="input"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </label>
          <label>
            To:
            <input
              type="date"
              className="input"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </label>
        </div>
      </div>

      {data.length > 0 && (
        <div className="section" ref={sectionRef}>
          <h4>📊 Product Comparison: {customer1.toUpperCase()} vs {customer2.toUpperCase() || ""}</h4>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="product" angle={0} interval={0} height={60} tick={{ fontSize: 12 }} />
              <YAxis />
              <Tooltip />
              <Legend verticalAlign="top" height={36} />
              {c1 && (
                <Bar dataKey={c1} fill="#007bff">
                  <LabelList dataKey={c1} position="top" style={{ fontSize: 12, fontWeight: "bold", fill: "#000" }} />
                </Bar>
              )}
              {c2 && (
                <Bar dataKey={c2} fill="#28a745">
                  <LabelList dataKey={c2} position="top" style={{ fontSize: 12, fontWeight: "bold", fill: "#000" }} />
                </Bar>
              )}
            </BarChart>
          </ResponsiveContainer>

          <h4>📋 Product Sales Table</h4>
          <table className="stock-table">
            <thead>
              <tr>
                <th>Product</th>
                {c1 && <th>{customer1.toUpperCase()}</th>}
                {c2 && <th>{customer2.toUpperCase()}</th>}
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx) => (
                <tr key={idx}>
                  <td>{row.product}</td>
                  {c1 && <td>{row[c1] || 0}</td>}
                  {c2 && <td>{row[c2] || 0}</td>}
                </tr>
              ))}
              <tr style={{ fontWeight: "bold" }}>
                <td>Total</td>
                {c1 && <td>{totals[c1]}</td>}
                {c2 && <td>{totals[c2]}</td>}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {data.length > 0 && (
        <button className="button" onClick={handlePrint}>
          🖨️ Print Graph & Table
        </button>
      )}
    </div>
  );
}
